import dynamic from 'next/dynamic';
import propTypes from 'prop-types';
import React, { Fragment } from 'react';
import buildLinkedDataTs from '@/linkedData/trustedShop-lkd';
import buildLinkedData from '@/linkedData/products-lkd';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { useVisibleProducts } from '@/hooks/useVisibleProducts';
import JsonLd from '@/components/common/JsonLd';
import { ESpotsLoader } from '@/components/layout/espots/ESpotsLoader';
import Push from '@/components/common/design-system/Push.ds';
import styles from './ProductListing.module.scss';
import { ESPOTS } from '@/constants/espot';
import { useServices } from '@/services/useServices';

const ProductCard = dynamic(() => import('@/components/categ/ProductCard'), { ssr: true });
ProductCard.displayName = 'ProductCard';

const noProductSpot = [ESPOTS.FILTERS_NOPRODUCTS];

export const VisibilityTrackingContext = React.createContext(null);

export const ProductListing = ({
  categ, products, onProductSelection, url, isMobile,
  storeId, sort, activeFilter, searchMode,
  storeConf, ratingValue, ratingCount, customClassName,
  espotListingPush, filterQuery, unitBottleOnly
}) => {
  const isMobileOrTablet = useScreenSizeContext().isMobileOrTablet;
  const { datalayerService } = useServices();

  const visibilityTracking = useVisibleProducts(
    datalayerService,
    products || [],
    storeConf,
    categ,
    filterQuery,
    sort,
    searchMode,
    false
  );

  const jsonData = buildLinkedData(url, products, isMobileOrTablet);
  const jsonTs = buildLinkedDataTs(storeConf, ratingValue, ratingCount);

  const buildProductCard = (product, position) => {
    return (
      <Fragment key={position}>
        {position === 3 && espotListingPush &&
          <div className={styles['push-container']}>
            <Push {...espotListingPush} />
          </div>
        }
        <ProductCard
          key={product._id}
          index={position}
          product={product}
          onClickProduct={onProductSelection}
          sort={sort}
          isCU={unitBottleOnly}
          activeFilter={activeFilter}
          categ={categ}
          searchMode={searchMode}
          position={position}
          isMobile={isMobile}
        />
      </Fragment>
    );
  };

  return (
    <>
      <JsonLd data={jsonData} />
      <JsonLd data={jsonTs} />
      <VisibilityTrackingContext.Provider value={visibilityTracking}>
        <div className={customClassName} data-test-id="product-listing" data-rbf="product-listing" sensefuel-id="product-listing">
          {products ? products.map(buildProductCard) : (
            <div className="col-xl-4 col-md-6 col-sm-8 py-3">
              <ESpotsLoader storeId={storeId} locations={noProductSpot} />
            </div>
          )}
        </div>
      </VisibilityTrackingContext.Provider>
    </>
  );
};

ProductListing.propTypes = {
  categ: propTypes.object,
  products: propTypes.arrayOf(propTypes.shape({
    _id: propTypes.string.isRequired
  })),
  sort: propTypes.number,
  onProductSelection: propTypes.func,
  url: propTypes.string,
  isMobile: propTypes.bool.isRequired,
  storeId: propTypes.string,
  activeFilter: propTypes.string,
  searchMode: propTypes.bool,
  customClassName: propTypes.string
};

export default ProductListing;
